/**
 * AiBoardCard — the nightly AI board, condensed for the Dashboard.
 *
 * The stated goal of this layer is that the landing page stops being a list of raw alerts and
 * starts leading with the few things worth acting on. This is that, kept deliberately small: it
 * summarises and links, it does not reproduce the AI Desk.
 *
 * Three honesty rules carried over from the Desk:
 *   - shadow mode is labelled wherever a verdict appears, never implied by absence
 *   - "no ideas" is stated as a real answer, not rendered as an empty box
 *   - a stale board says how old it is rather than passing itself off as today's
 *
 * It never triggers a run and never calls a model, so the Dashboard cannot spend money.
 */
import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Chip, Button, Tooltip, Skeleton, Divider } from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useAppTheme } from '../../hooks/useAppTheme';
import aiService, { type AiBoard } from '../../services/ai.service';
import { useAiCost, inr } from '../../hooks/useAiCost';

const STATE_LABEL: Record<string, string> = {
  BUY_NOW: 'Act now',
  BUY_ON_PULLBACK: 'On pullback',
  WAIT_FOR_CONFIRMATION: 'Wait',
  WATCH: 'Watch',
  AVOID: 'Avoid',
};

const AiBoardCard: React.FC = () => {
  const { C } = useAppTheme();
  const navigate = useNavigate();
  const { report: cost } = useAiCost();
  const [data, setData] = useState<AiBoard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    aiService.getBoard()
      .then(b => { if (!cancelled) setData(b); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Nothing has ever run: stay out of the way entirely rather than advertising an empty feature.
  // Spend counts as "something happened" — an on-demand ask costs money without producing a run,
  // and hiding the card would hide the only place that spend is reported.
  if (!loading && !data?.run && !(cost && cost.spend.last30Usd > 0)) return null;

  const run = data?.run || null;
  const shadow = run?.mode === 'SHADOW';
  const board = data?.board || [];

  const ageHours = run?.createdAt
    ? Math.floor((Date.now() - new Date(run.createdAt).getTime()) / 3_600_000)
    : null;
  const stale = ageHours != null && ageHours > 30;
  // Audit A-022: past 3 days the picks are history, not a board. Showing a 43-day-old
  // "On pullback" list at the top of the Dashboard read as today's ideas.
  const veryStale = ageHours != null && ageHours > 72;
  const ageText = ageHours == null ? '' : ageHours >= 48 ? `${Math.floor(ageHours / 24)} days` : `${ageHours}h`;

  const gradeColor = (g: string | null) =>
    !g ? C.dim
      : g.startsWith('A') ? C.bull
        : g.startsWith('B') ? C.neutral
          : g === 'C' ? C.warn
            : C.bear;

  return (
    <Paper sx={{
      p: 2, mb: 2, borderRadius: 2, boxShadow: 2,
      borderLeft: `4px solid ${C.accent}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <PsychologyIcon sx={{ color: C.accent, fontSize: 20 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>AI Desk</Typography>
        {shadow && (
          <Tooltip arrow title="Under evaluation. Everything here is recorded and scored, but nothing is a recommendation yet.">
            <Chip size="small" label="SHADOW" sx={{
              height: 18, fontSize: '0.6rem', fontWeight: 800,
              bgcolor: C.warnMuted, color: C.warn,
            }} />
          </Tooltip>
        )}
        {stale && (
          <Tooltip arrow title="The most recent completed run is more than a day old. It is shown because it is the latest there is, not because it is current.">
            <Chip size="small" label={`${ageText} old`} sx={{
              height: 18, fontSize: '0.6rem', fontWeight: 700,
              bgcolor: C.dimMuted, color: C.dim,
            }} />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/ai-desk')}
          sx={{ fontWeight: 700 }}>
          Open
        </Button>
      </Box>

      {loading ? (
        <>
          <Skeleton height={22} /><Skeleton height={22} width="80%" />
        </>
      ) : veryStale ? (
        <Typography variant="body2" data-testid="ai-board-stale" sx={{ color: C.dim }}>
          {cost && !cost.guard.enabled
            ? <>The AI layer is switched off in Settings → AI, so there are no new picks. The last run was {ageText} ago; its picks are not shown here as if they were current.</>
            : <>The AI Desk has not completed a run for {ageText}, so its last picks are not shown here as
              if they were current. Open the AI Desk to see why it stopped (scheduler, spend cap or key).</>}
        </Typography>
      ) : !board.length ? (
        <Typography variant="body2" sx={{ color: C.dim }}>
          Nothing cleared the bar in the last run — {run?.candidateCount ?? 0} symbols signalled,
          none worth acting on. That is a real answer, not a failure.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {board.slice(0, 3).map(c => (
            <Box key={c.id}
              onClick={() => navigate(`/charts/${encodeURIComponent(c.symbol)}?exchange=${encodeURIComponent(c.exchange)}`)}
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1, cursor: 'pointer',
                p: 0.6, borderRadius: 0.8, '&:hover': { bgcolor: C.neutralMuted },
              }}>
              <Chip size="small" label={c.grade || '—'} sx={{
                height: 19, minWidth: 30, fontWeight: 800, fontSize: '0.65rem',
                bgcolor: `${gradeColor(c.grade)}22`, color: gradeColor(c.grade),
              }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.3 }}>
                  {c.symbol}
                  <Typography component="span" variant="caption" sx={{ color: C.dim, ml: 0.7 }}>
                    {c.state ? STATE_LABEL[c.state] || c.state : ''}
                    {c.conviction != null ? ` · conviction ${c.conviction}` : ''}
                  </Typography>
                </Typography>
                {c.summary && (
                  <Typography variant="caption" sx={{
                    color: C.dim, display: '-webkit-box', WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.35,
                  }}>
                    {c.summary}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
          {board.length > 3 && (
            <Typography variant="caption" sx={{ color: C.dim, pl: 0.6 }}>
              +{board.length - 3} more on the AI Desk
            </Typography>
          )}
        </Box>
      )}

      {!loading && run && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" sx={{ color: C.dim }}>
            {run.universeSize} scanned · {run.candidateCount} signalled · {run.clusterCount} distinct bets
            {run.shortlistedCount ? ` · ${run.shortlistedCount} reviewed` : ''} · opinion only, stop and
            size come from the risk engine
          </Typography>
        </>
      )}

      {/* ── What it is costing ───────────────────────────────────────────────
          Inside this card rather than as a new one: spend is a property of the AI layer, and a
          separate tile on an already-dense dashboard would earn its space less than one line here
          does. Every figure comes from the same endpoint the cost hints on the buttons read, so
          the two can never disagree. */}
      {cost && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tooltip arrow title={
              <span>
                Measured from the call ledger, not estimated.
                {cost.model.selected && <><br />Model: {cost.model.selected}
                  {cost.model.price && <> at ${cost.model.price.inPerM}/${cost.model.price.outPerM} per million tokens</>}
                  {cost.model.priceEstimated && ' (rate estimated from its family)'}</>}
                {cost.efficiency.perEvaluatedCandidateUsd != null && (
                  <><br />{inr(cost.efficiency.perEvaluatedCandidateUsd)} per candidate the model
                    looked at, {cost.efficiency.perActionableDecisionUsd != null
                      ? `${inr(cost.efficiency.perActionableDecisionUsd)} per idea that reached the board`
                      : 'no board ideas yet'} — over the last {cost.efficiency.windowDays} days.</>
                )}
                {cost.notes.map((n, i) => <React.Fragment key={i}><br /><br />{n}</React.Fragment>)}
              </span>
            }>
              <Typography variant="caption" sx={{ color: C.dim, cursor: 'help', fontWeight: 700 }}>
                AI spend
              </Typography>
            </Tooltip>
            <Typography variant="caption" sx={{ color: C.dim, fontVariantNumeric: 'tabular-nums' }}>
              today <b style={{ color: cost.guard.capReached ? C.bear : 'inherit' }}>
                {inr(cost.spend.todayUsd)}</b>
              {' · '}7d <b>{inr(cost.spend.last7Usd)}</b>
              {' · '}30d <b>{inr(cost.spend.last30Usd)}</b>
              {cost.guard.capUsd > 0 && <> · cap {inr(cost.guard.capUsd)}/day</>}
            </Typography>
            {!cost.guard.enabled && (
              <Tooltip arrow title="The AI layer is switched off in Settings → AI. Nothing is being spent.">
                <Chip size="small" label="OFF" sx={{
                  height: 17, fontSize: '0.58rem', fontWeight: 800,
                  bgcolor: C.dimMuted, color: C.dim,
                }} />
              </Tooltip>
            )}
            {cost.guard.capReached && (
              <Tooltip arrow title="Today's cap has been reached. No further calls will be made until tomorrow.">
                <Chip size="small" label="CAP REACHED" sx={{
                  height: 17, fontSize: '0.58rem', fontWeight: 800,
                  bgcolor: C.warnMuted, color: C.warn,
                }} />
              </Tooltip>
            )}
            {cost.model.tierNote && (
              <Tooltip arrow title={cost.model.tierNote}>
                <Chip size="small" label="MODEL CHANGED" sx={{
                  height: 17, fontSize: '0.58rem', fontWeight: 800,
                  bgcolor: C.warnMuted, color: C.warn,
                }} />
              </Tooltip>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default AiBoardCard;
